import React, { Fragment, useMemo, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import BatchInvoiceSummary from './BatchInvoiceSummary';

import DataRow from 'components/Shared/DataRow/DataRow';
import Hr from 'components/Shared/Hr/Hr';
import { Chevron } from 'components/Shared/Icons/Icons';
import { colors, breakpoints, fontSize } from 'styles/cp';

export interface BatchInvoiceSummaryProps {
  batchInvoices: any[];
  maxSelectableBalance?: number;
  partialPaymentAmount?: number | null;
}

const BatchInvoiceInfo = ({
  batchInvoices,
  maxSelectableBalance,
  partialPaymentAmount = null,
}: BatchInvoiceSummaryProps) => {
  const { selectedInvoices, totalAmount } = useMemo(() => {
    const selected = batchInvoices.filter((invoice) => invoice.selected);
    // Calculate total using the actual payment amount for each invoice
    const total = selected.reduce((sum, invoice) => {
      // For default invoice with partial payment, use the partial amount
      if (invoice.defaultInvoice && partialPaymentAmount !== null && partialPaymentAmount > 0) {
        return sum + partialPaymentAmount;
      }
      // For invoices over the limit, use the capped amount
      const cappedAmount =
        maxSelectableBalance && invoice.balance > maxSelectableBalance
          ? maxSelectableBalance
          : invoice.balance;
      return sum + cappedAmount;
    }, 0);
    return { selectedInvoices: selected, totalAmount: total };
  }, [batchInvoices, maxSelectableBalance, partialPaymentAmount]);
  const [showAllInvoices, setShowAllInvoices] = useState(false);
  const displayedInvoices = useMemo(() => {
    return showAllInvoices ? selectedInvoices : selectedInvoices.slice(0, 6);
  }, [showAllInvoices, selectedInvoices]);
  const currency = 'USD';

  const invoiceSummaries = useMemo(() => {
    return displayedInvoices.map((invoice, index) => {
      // Use capped amount for invoices over the limit
      const isCapped = maxSelectableBalance && invoice.balance > maxSelectableBalance;
      const displayAmount = isCapped ? maxSelectableBalance : invoice.balance;

      // Calculate outstanding balance for default invoice
      // Show outstanding balance in two cases:
      // 1. Invoice is capped due to max limit (show remaining balance)
      // 2. User edited the amount to be partial (show remaining balance)
      let invoiceAmount = displayAmount;
      let outstandingBalance = null;

      if (invoice.defaultInvoice) {
        // For default invoice, always show the original balance in the list
        // Only the total should reflect the edited amount
        if (isCapped) {
          // Invoice is over limit, show capped amount and outstanding
          invoiceAmount = maxSelectableBalance;
          outstandingBalance = invoice.balance - maxSelectableBalance;
        }
        // Note: We don't change invoiceAmount for edited amounts here
        // The individual invoice shows original balance, but total uses edited amount
      }

      return (
        <BatchInvoiceSummary
          key={invoice.docNumber || invoice.saleId || index}
          invoiceNumber={invoice.docNumber || invoice.saleId || 'N/A'}
          invoiceAmount={invoiceAmount}
          currency={currency}
          outstandingBalance={outstandingBalance}
        />
      );
    });
  }, [displayedInvoices, maxSelectableBalance, partialPaymentAmount]);

  // Empty state when no invoices are selected
  if (selectedInvoices.length === 0) {
    return (
      <Fragment key="batch-payments-empty">
        <Hr marginTop={10} marginBottom={10} borderColor={colors.offWhite} opacity={1} />

        <div className="invoice-summary">
          <span className="invoice-batch-summary">
            <FormattedMessage id="BATCH_PAYMENTS_SUMMARY_INVOICES" defaultMessage="Summary" />
          </span>
        </div>

        <li aria-label="empty state" className="empty-state-row">
          <div className="empty-state-content">
            <FormattedMessage
              id="BATCH_PAYMENTS_NO_INVOICES_SELECTED"
              defaultMessage="No invoices selected"
            />
          </div>
        </li>

        <Hr marginTop={10} marginBottom={10} borderColor={colors.offWhite} opacity={1} />

        <div className="invoice-total-row">
          <DataRow
            idValue="INVOICE_TOTAL"
            defaultMessage="Total"
            bold={true}
            color={colors.black}
            value={<FormattedNumber value={0} style="currency" currency={currency} />}
          />
        </div>

        <style jsx>{`
          .invoice-summary {
            padding: 0;
            margin: 0 0 10px 0;

            @media (max-width: ${breakpoints.md}) {
              max-width: 400px;
              margin: 5px auto;
            }
            @media (max-width: ${breakpoints.sm}) {
              max-width: 576px;
            }
          }
          .invoice-batch-summary {
            display: flex;
            justify-content: space-between;
            font-size: ${fontSize.xs};
            font-family: AvenirNextforINTUIT-Demi;
          }
          .empty-state-row {
            display: block;
            margin: 6px 0px;
          }
          .empty-state-content {
            color: ${colors.gray};
            font-size: 14px;
            font-family: AvenirNextforINTUIT-Regular;
          }
        `}</style>
      </Fragment>
    );
  }

  return (
    <Fragment key="batch-payments">
      <Hr marginTop={10} marginBottom={10} borderColor={colors.offWhite} opacity={1} />

      <div className="invoice-summary">
        <span className="invoice-batch-summary">
          <FormattedMessage id="BATCH_PAYMENTS_SUMMARY_INVOICES" defaultMessage="Summary" />
        </span>
      </div>

      {invoiceSummaries}

      {selectedInvoices.length > 6 && (
        <div className="show-all-text-container">
          <span className="show-all-text">
            {showAllInvoices ? (
              <FormattedMessage id="SHOW_LESS_ITEMS" defaultMessage="Show less items" />
            ) : (
              <FormattedMessage id="SHOW_ALL_ITEMS" defaultMessage="Show all items" />
            )}
          </span>
          <span className="chevron-down" onClick={() => setShowAllInvoices(!showAllInvoices)}>
            <Chevron rotate={showAllInvoices ? 180 : 0} />
          </span>
        </div>
      )}

      <Hr marginTop={10} marginBottom={10} borderColor={colors.offWhite} opacity={1} />

      <div className="invoice-total-row">
        <DataRow
          idValue="INVOICE_TOTAL"
          defaultMessage="Total"
          bold={true}
          color={colors.black}
          value={<FormattedNumber value={totalAmount} style="currency" currency={currency} />}
        />
      </div>

      <style jsx>{`
        .invoice-summary {
          padding: 0;
          margin: 0 0 10px 0;
          max-height: 525px;
          overflow: auto;

          @media (max-width: ${breakpoints.md}) {
            max-width: 400px;
            margin: 5px auto;
          }
          @media (max-width: ${breakpoints.sm}) {
            max-width: 576px;
          }
        }
        .invoice-batch-summary {
          display: flex;
          justify-content: space-between;
          font-size: ${fontSize.xs};
          font-family: AvenirNextforINTUIT-Demi;
        }
        .show-all-text-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .show-all-text {
          font-family: AvenirNextforINTUIT-Regular;
          font-weight: normal;
          font-size: ${fontSize.xs};
        }
        .chevron-down {
          text-align: right;
        }
      `}</style>
    </Fragment>
  );
};

export default BatchInvoiceInfo;
